/**
 * ScanAdapterPanel — renders inside KycPanel beneath the ID-verification
 * controls.  Surfaces the two adapter pipelines:
 *
 *   1. Background Check Plus (Checkr-style vendor)
 *   2. Content Screening (Sightengine/Hive-style vendor)
 *
 * Both UIs follow the same pattern: status badge, last-attempt
 * metadata, retry button (rate-limited server-side), and an inline
 * help line that explains *why* the action is gated.
 *
 * The component is presentation-only.  Eligibility, retries and
 * status mutations are enforced by the request-bgc-scan edge function
 * and the fn_request_bgc / fn_request_content_scan RPCs.
 */
import { useMemo } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  RotateCw,
  Image as ImageIcon,
  Mic,
  Video,
  FileWarning,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  useBackgroundCheck,
  type BgcStatus,
  type BackgroundCheckRow,
} from "@/lib/safety/useBackgroundCheck";
import {
  useContentScans,
  type ContentScanRow,
  type ContentScanStatus,
  type ContentScanTarget,
} from "@/lib/safety/useContentScans";
import { useProfile } from "@/lib/profile/useProfile";

// ----------------------------------------------------- BGC status mapping

const BGC_BADGE: Record<BgcStatus, { label: string; tone: string; icon: typeof CheckCircle2 }> = {
  not_started: {
    label: "Not started",
    tone: "border-border text-muted-foreground",
    icon: ShieldAlert,
  },
  requested: {
    label: "Queued",
    tone: "bg-muted text-muted-foreground",
    icon: Clock,
  },
  in_progress: {
    label: "Running",
    tone: "bg-muted text-muted-foreground",
    icon: Loader2,
  },
  requires_input: {
    label: "Action needed",
    tone: "bg-destructive/10 text-destructive border border-destructive/30",
    icon: ShieldAlert,
  },
  passed: {
    label: "Passed",
    tone: "bg-success/10 text-success",
    icon: ShieldCheck,
  },
  failed: {
    label: "Did not pass",
    tone: "bg-destructive/10 text-destructive border border-destructive/30",
    icon: ShieldAlert,
  },
  expired: {
    label: "Expired",
    tone: "bg-muted text-muted-foreground",
    icon: Clock,
  },
  canceled: {
    label: "Canceled",
    tone: "bg-muted text-muted-foreground",
    icon: ShieldAlert,
  },
};

// ----------------------------------------------------- Content scan mapping

const SCAN_BADGE: Record<ContentScanStatus, { label: string; tone: string }> = {
  queued: { label: "Queued", tone: "bg-muted text-muted-foreground" },
  in_progress: { label: "Scanning", tone: "bg-muted text-muted-foreground" },
  passed: { label: "Clean", tone: "bg-success/10 text-success" },
  flagged: {
    label: "Flagged",
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  },
  failed: {
    label: "Removed",
    tone: "bg-destructive/10 text-destructive border border-destructive/30",
  },
  manual_review: {
    label: "Reviewing",
    tone: "bg-muted text-muted-foreground",
  },
};

const TARGET_ICON: Record<ContentScanTarget, typeof ImageIcon> = {
  avatar: ImageIcon,
  photo: ImageIcon,
  voice_intro: Mic,
  video_intro: Video,
  message_attachment: FileWarning,
};

const TARGET_LABEL: Record<ContentScanTarget, string> = {
  avatar: "Avatar",
  photo: "Photo",
  voice_intro: "Voice intro",
  video_intro: "Video intro",
  message_attachment: "Message attachment",
};

const refTail = (ref: string) => {
  const tail = ref.split("/").pop() ?? ref;
  return tail.length > 22 ? `…${tail.slice(-20)}` : tail;
};

// ============================================================ BGC tile

interface BgcTileProps {
  row: BackgroundCheckRow | null;
  busy: boolean;
  onStart: () => void;
}

const BgcTile = ({ row, busy, onStart }: BgcTileProps) => {
  const status: BgcStatus = row?.status ?? "not_started";
  const meta = BGC_BADGE[status];
  const Icon = meta.icon;
  const isInFlight = status === "requested" || status === "in_progress";

  const ctaLabel = (() => {
    if (status === "passed") return "Re-run";
    if (status === "requires_input") return "Continue";
    if (status === "failed" || status === "expired") return "Try again";
    if (isInFlight) return "Running…";
    return "Start background check";
  })();

  return (
    <div className="rounded-xl border border-border bg-card/40 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">Background Check Plus</h4>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}
            >
              <Icon className={`h-3 w-3 ${status === "in_progress" ? "animate-spin" : ""}`} />
              {meta.label}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground max-w-prose">
            Optional criminal-records and sex-offender registry check.
            Powered by an accredited screening vendor. We never store the
            raw report — only a pass/fail flag and a 12-month expiry.
          </p>
        </div>
        <Button size="sm" onClick={onStart} disabled={busy || isInFlight}>
          {(busy || isInFlight) ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <RotateCw className="h-3.5 w-3.5 mr-1" />
          )}
          {ctaLabel}
        </Button>
      </div>

      {row && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          {row.requested_at && (
            <>
              <dt>Requested</dt>
              <dd className="text-right tabular-nums">
                {new Date(row.requested_at).toLocaleDateString()}
              </dd>
            </>
          )}
          {row.completed_at && (
            <>
              <dt>Completed</dt>
              <dd className="text-right tabular-nums">
                {new Date(row.completed_at).toLocaleDateString()}
              </dd>
            </>
          )}
          {row.expires_at && (
            <>
              <dt>Expires</dt>
              <dd className="text-right tabular-nums">
                {new Date(row.expires_at).toLocaleDateString()}
              </dd>
            </>
          )}
          <dt>Attempts</dt>
          <dd className="text-right tabular-nums">{row.attempts}</dd>
        </dl>
      )}

      {row?.failure_reason && (
        <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] text-destructive">
          {row.failure_reason}
        </p>
      )}

      {!row && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          You haven't run a background check yet. Required to host events
          and apply for community-verified status.
        </p>
      )}
    </div>
  );
};

// =================================================== Content scan list

interface ScanRowProps {
  row: ContentScanRow;
  busy: boolean;
  onRescan: () => void;
}

const ScanRow = ({ row, busy, onRescan }: ScanRowProps) => {
  const Icon = TARGET_ICON[row.target_kind];
  const meta = SCAN_BADGE[row.status];
  const canRescan =
    row.target_kind !== "message_attachment" &&
    (row.status === "flagged" || row.status === "failed" || row.status === "passed");

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-card/30 p-3">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-secondary text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-medium">{TARGET_LABEL[row.target_kind]}</p>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}
          >
            {meta.label}
          </span>
        </div>
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={row.target_ref}>
          {refTail(row.target_ref)}
        </p>
        {row.flags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {row.flags.map((f) => (
              <span
                key={f}
                className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-400"
              >
                {f.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}
      </div>
      {canRescan && (
        <Button size="sm" variant="outline" onClick={onRescan} disabled={busy} className="h-7 text-[11px]">
          {busy ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RotateCw className="h-3 w-3 mr-1" />
          )}
          Rescan
        </Button>
      )}
    </li>
  );
};

// =================================================== Top-level panel

const ScanAdapterPanel = () => {
  const { profile } = useProfile();
  const bgc = useBackgroundCheck();
  const scans = useContentScans();

  const handleStart = async () => {
    const result = await bgc.start();
    if (result.notEntitled) {
      toast.error(
        "Background Check Plus is required. Add it from à la carte features.",
      );
      return;
    }
    if (result.rateLimited) {
      toast.warning("You can retry a background check every 30 minutes.");
      return;
    }
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success("Background check started.");
  };

  const handleRescan = async (kind: ContentScanTarget, ref: string) => {
    const result = await scans.rescan(kind, ref);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success("Rescan queued.");
  };

  // Synthesise a "no scan yet" placeholder row for each owned asset
  // so members can see what *will* be screened.
  const ownedAssets = useMemo(() => {
    const items: { kind: ContentScanTarget; ref: string }[] = [];
    if (profile?.avatar_url) items.push({ kind: "avatar", ref: profile.avatar_url });
    for (const p of profile?.photos ?? []) items.push({ kind: "photo", ref: p });
    if (profile?.voice_intro_url)
      items.push({ kind: "voice_intro", ref: profile.voice_intro_url });
    if (profile?.video_intro_url)
      items.push({ kind: "video_intro", ref: profile.video_intro_url });
    return items;
  }, [profile]);

  const merged = useMemo(() => {
    return ownedAssets.map((a) => {
      const key = `${a.kind}:${a.ref}`;
      const existing = scans.latestByTarget.get(key);
      return { asset: a, scan: existing ?? null };
    });
  }, [ownedAssets, scans.latestByTarget]);

  const loading = bgc.loading || scans.loading;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h3 className="text-sm font-semibold">Background &amp; content checks</h3>
        <p className="text-xs text-muted-foreground max-w-prose">
          Optional vendor-powered checks that earn additional badges.
          Background checks unlock event hosting; content screening keeps
          your profile media policy-compliant.
        </p>
      </header>

      {loading ? (
        <div className="rounded-xl border border-border bg-card/40 p-4 animate-pulse">
          <div className="h-3 w-40 bg-muted rounded" />
          <div className="mt-3 h-3 w-full bg-muted rounded" />
        </div>
      ) : (
        <BgcTile row={bgc.row} busy={bgc.busy} onStart={handleStart} />
      )}

      <div>
        <h4 className="text-xs font-semibold mb-2">Profile media screening</h4>
        {merged.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            Add photos, a voice intro, or a video intro and we'll show their
            screening status here.
          </p>
        ) : (
          <ul className="space-y-2">
            {merged.map(({ asset, scan }) => {
              const key = `${asset.kind}:${asset.ref}`;
              const busy = scans.busyTarget === key;
              if (scan) {
                return (
                  <ScanRow
                    key={scan.id}
                    row={scan}
                    busy={busy}
                    onRescan={() => void handleRescan(asset.kind, asset.ref)}
                  />
                );
              }
              // No scan row yet → render a "not screened yet" tile with
              // a manual rescan trigger so members can self-serve.
              const Icon = TARGET_ICON[asset.kind];
              return (
                <li
                  key={key}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card/30 p-3"
                >
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-secondary text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-medium">
                        {TARGET_LABEL[asset.kind]}
                      </p>
                      <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        Not screened
                      </span>
                    </div>
                    <p
                      className="mt-0.5 truncate text-[11px] text-muted-foreground"
                      title={asset.ref}
                    >
                      {refTail(asset.ref)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleRescan(asset.kind, asset.ref)}
                    disabled={busy}
                    className="h-7 text-[11px]"
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RotateCw className="h-3 w-3 mr-1" />
                    )}
                    Scan now
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Scan results power Discovery visibility and event eligibility.
        Final decisions are enforced server-side; this view reflects the
        most recent vendor result on file.
      </p>
    </div>
  );
};

export default ScanAdapterPanel;
